// SEO head helper — injects title, meta description, OpenGraph/Twitter,
// canonical URL, and arbitrary JSON-LD schema blocks for any public page.
// Uses react-helmet-async (client-side injection — modern Googlebot renders
// JS fully and picks these up on first crawl).
//
// Usage:
//   <SeoHead
//     title="Today's Best Alberta Mortgage Rates · Hello Mortgage"
//     description="…"
//     path="/rates/5-yr-fixed"
//     schema={[jsonLdObject, ...]}
//   />
import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://www.hellomortgage.ca";
const SITE_NAME = "Hello Mortgage";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`;

export default function SeoHead({
  title,
  description,
  path = "/",
  noindex = false,
  canonicalUrl = null,
  follow = true,
  schema = null,
  ogImage,
  siteUrl,
  siteName = SITE_NAME,
}) {
  const canonical = canonicalUrl || `${siteUrl || SITE_URL}${path}`;
  const showCanonical = !noindex || Boolean(canonicalUrl);  // a noindex page only declares a canonical when it points somewhere else
  const schemaBlocks = Array.isArray(schema) ? schema : schema ? [schema] : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {showCanonical && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content={follow ? "noindex,follow" : "noindex,nofollow"} />}

      {/* OpenGraph (Facebook / LinkedIn / iMessage previews) */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage || DEFAULT_OG_IMAGE} />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage || DEFAULT_OG_IMAGE} />

      {/* JSON-LD schema blocks */}
      {schemaBlocks.map((block, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}

// Human-friendly "Updated Month D, YYYY" used across meta descriptions so
// SERPs show the freshness signal.
export function formatMetaDate(iso) {
  if (!iso) {
    return new Date().toLocaleDateString("en-CA", {
      month: "long", day: "numeric", year: "numeric",
    });
  }
  try {
    return new Date(iso).toLocaleDateString("en-CA", {
      month: "long", day: "numeric", year: "numeric",
    });
  } catch {
    return "";
  }
}

// Shared Hello Mortgage LocalBusiness schema — included on the homepage
// so Google knows this is an Alberta-based mortgage brokerage. Enables
// local-search rich results.
export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FinancialService",
  "name": SITE_NAME,
  "url": SITE_URL,
  "areaServed": [
    { "@type": "State", "name": "Alberta" },
    { "@type": "Country", "name": "Canada" },
  ],
  "serviceType": "Mortgage brokerage",
};

const CP_MONTHS = ["Jan.", "Feb.", "March", "April", "May", "June", "July", "Aug.", "Sept.", "Oct.", "Nov.", "Dec."];
// Canadian Press style date, e.g. Sept. 16, 2026
export function cpDate(iso) {
  const d = iso ? new Date(iso) : new Date();
  return `${CP_MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}
